/**
 * Whether a piece of LaTeX may be typeset from markup, when it came from a person.
 *
 * ## The decision this file exists to record
 *
 * `MathText` renders MathLive's HTML with `dangerouslySetInnerHTML`, and its note says the
 * reasoning holds because everything it is given is a compile-time constant — *"If this is ever
 * pointed at something a user typed, that reasoning stops holding and the decision has to be
 * made again."* Previewing a problem statement points it at exactly that, so here is the
 * decision.
 *
 * MathLive implements `\href`, `\class` and `\htmlData`, which exist to put attributes into the
 * output. That was checked in the installed package rather than assumed. So the markup path is
 * a real injection surface, and the question is what to do about it.
 *
 * ## Why the answer is not "the user only attacks themselves"
 *
 * That is true of typing and false of **pasting**, which is the workflow this whole feature is
 * for: a teacher moves a problem across from a website, a document, an old question bank. The
 * text arrives from somewhere else and lands in their page. It is a thin thread, but it is the
 * one thread the feature deliberately pulls on.
 *
 * ## Why a refusal rather than a filter
 *
 * Stripping the dangerous commands would be a denylist, and a denylist over somebody else's
 * macro language is a promise this file cannot keep — the next version of MathLive may add a
 * fourth command and nothing here would know.
 *
 * So a formula containing one is *not previewed*: it is shown as its own source, the way an
 * unrenderable formula already is. That costs a preview for a formula no geometry problem
 * contains, and it cannot be wrong about a formula it never renders.
 *
 * The string is still sent to the model exactly as typed. This governs what is drawn into the
 * page, which is the only place the risk lives.
 */

/**
 * The commands that put author-controlled attributes into MathLive's output.
 *
 * Matched on the command name with a boundary, so `\hrefsomething` is not one of these and
 * `\href` written `\\href` — an escaped backslash followed by text — is not either.
 */
const INJECTING = /\\(href|class|htmlData|htmlStyle|cssId)\b/;

/** Whether this formula may be turned into markup and put into the page. */
export function safeToTypeset(latex: string): boolean {
  return !INJECTING.test(latex);
}
